<template>
    <div class="mb-4">
        <h2
            class="text-lg font-medium leading-7 tracking-[-0.02em] text-iron-700"
        >
            {{ $t("onboarding.connect_calendar") }}
        </h2>

        <div class="mt-8 grid gap-1">
            <Label> {{ $t("onboarding.add_events_to") }} </Label>
            <Select
                :disabled="loading"
                :model-value="selectedIntegrationCalendar"
                @update:model-value="onSelectedIntegrationUpdate"
                v-if="integrationCalendars.length > 0"
            >
                <SelectTrigger class="w-full">
                    <SelectValue
                        :placeholder="
                            $t('event_types.calendars.choose_calendar')
                        "
                    />
                </SelectTrigger>
                <SelectContent>
                    <SelectGroup
                        v-for="calendarIntegration of integrationCalendars"
                    >
                        <SelectLabel class="flex items-center gap-2 pl-2">
                            <GoogleCalendar
                                v-if="
                                    calendarIntegration.integration.type ===
                                    'GOOGLE'
                                "
                            />

                            <div class="text-xs">
                                <p>
                                    {{ calendarIntegration.integration.name }}
                                </p>
                                <p class="text-gray-600">
                                    {{ calendarIntegration.integrationEmail }}
                                </p>
                            </div>
                        </SelectLabel>
                        <SelectItem
                            v-for="calendar of calendarIntegration.calendars"
                            :value="`${calendarIntegration.integration.id}-${calendar.id.toString()}`"
                        >
                            {{ calendar.name }}
                        </SelectItem>
                    </SelectGroup>
                </SelectContent>
            </Select>
        </div>
    </div>
</template>

<script lang="ts" setup>
import axios from "axios";
import { onMounted, ref } from "vue";
import route from "ziggy-js";
import { router } from "@inertiajs/vue3";
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectLabel,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

import { Label } from "@/components/ui/label";

import type {
    AssignIntegrationRequest,
    Calendar,
    IntegrationCalendar,
} from "../../EventTypes/types/IntegrationCalendar";
import GoogleCalendar from "../../Integrations/components/logos/GoogleCalendar.svg";

import type { EventType } from "../../EventTypes/types/EventType";
import { toast } from "@/components/ui/toast";

interface Props {
    eventType: EventType;
}

const props = defineProps<Props>();

const integrationCalendars = ref<IntegrationCalendar[]>([]);
const integrationCalendarsLoaded = ref(false);
const selectedIntegrationCalendar = ref<string>();
const loading = ref(false);

onMounted(async () => {
    integrationCalendars.value = await getIntegrationCalendars();
    integrationCalendarsLoaded.value = true;
});

async function getIntegrationCalendars() {
    try {
        const res = await axios.get(route("app.integrations.calendars.index"));

        return res.data.data;
    } catch (err) {
        toast({
            title: "Uh oh! Something went wrong. Please contact support.",
            description: "There was a problem with your request.",
            variant: "destructive",
        });
    }
}

function onSelectedIntegrationUpdate(value: string) {
    const splitText = value.split("-");

    if (splitText.length !== 2) {
        toast({
            title: `Error when assigning calendar`,
            description: `Split chunk is too long, expected 2, got ${splitText.length}`,
            variant: "destructive",
        });

        return;
    }

    const [integrationId, calendarId] = splitText;

    const integrationCalendar = integrationCalendars.value.find(
        (integrationCalendar) => {
            return (
                integrationCalendar.integration.id === parseInt(integrationId)
            );
        },
    );

    if (!integrationCalendar) {
        toast({
            title: `Error when assigning calendar`,
            description: `Integration calendar not found`,
            variant: "destructive",
        });
        return;
    }

    const calendar = integrationCalendar.calendars.find(
        (calendar) => calendar.id.toString() === calendarId,
    );

    if (!calendar) {
        toast({
            title: `Error when assigning calendar`,
            description: `Calendar not found`,
            variant: "destructive",
        });
        return;
    }

    assignIntegrationCalendar(parseInt(integrationId), calendar);
}

function assignIntegrationCalendar(
    calendarIntegrationId: number,
    calendar: Calendar,
) {
    loading.value = true;

    const data: AssignIntegrationRequest = {
        calendars: [
            {
                integration_id: calendarIntegrationId,
                integration_calendar_id: calendar.id,
                integration_calendar_name: calendar.name,
                integration_calendar_default: calendar.default,
            },
        ],
    };

    router.post(
        route("app.event-types.calendar-integrations.assign", {
            eventType: props.eventType.id,
        }),
        data,
        {
            onFinish: () => {
                loading.value = false;
            },
            onError: (err) => {
                toast({
                    title: "Uh oh! Something went wrong. Please contact support.",
                    description:
                        "There was a problem with your request." +
                        JSON.stringify(err),
                    variant: "destructive",
                });
            },
        },
    );
}
</script>
